import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, map } from 'rxjs';
import { skipLoaderHeaders } from 'app/core/interceptors/loader.interceptor';
import {
  AnnouncementPlatform,
  AnnouncementStats,
  AnnouncementStatus,
  HistoryAnnouncement,
  PendingAnnouncement,
  PlatformAnnouncement,
  PlatformAnnouncementInput,
} from '@interfaces/PlatformAnnouncement';

/** sessionStorage flag: login notices already requested in this browser session. */
export const PLATFORM_NOTICES_SESSION_KEY = 'gd_platform_notices_checked';

/** Platform admin CRUD + end-user state of platform announcements. */
@Injectable({ providedIn: 'root' })
export class PlatformAnnouncementsService {
  private readonly adminUrl = `${environment.apiUrl}user/platform-admin/announcements`;
  private readonly userUrl = `${environment.apiUrl}user/announcements`;

  constructor(private readonly http: HttpClient) {}

  // --- Platform admin ---

  list(status?: AnnouncementStatus | ''): Observable<PlatformAnnouncement[]> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    return this.http
      .get<{
        announcements: PlatformAnnouncement[];
      }>(this.adminUrl, { params, headers: skipLoaderHeaders() })
      .pipe(map((response) => response.announcements ?? []));
  }

  create(input: PlatformAnnouncementInput): Observable<PlatformAnnouncement> {
    return this.http
      .post<{ announcement: PlatformAnnouncement }>(this.adminUrl, input, {
        headers: skipLoaderHeaders(),
      })
      .pipe(map((response) => response.announcement));
  }

  update(
    id: string,
    input: Partial<PlatformAnnouncementInput>,
  ): Observable<PlatformAnnouncement> {
    return this.http
      .patch<{
        announcement: PlatformAnnouncement;
      }>(`${this.adminUrl}/${encodeURIComponent(id)}`, input, {
        headers: skipLoaderHeaders(),
      })
      .pipe(map((response) => response.announcement));
  }

  /** Publishes a draft or resumes a paused announcement. */
  publish(id: string): Observable<PlatformAnnouncement> {
    return this.changeStatus(id, 'publish');
  }

  /** Hides a published announcement without losing who already saw it. */
  pause(id: string): Observable<PlatformAnnouncement> {
    return this.changeStatus(id, 'pause');
  }

  /** Back to draft from published or paused. */
  unpublish(id: string): Observable<PlatformAnnouncement> {
    return this.changeStatus(id, 'unpublish');
  }

  archive(id: string): Observable<PlatformAnnouncement> {
    return this.changeStatus(id, 'archive');
  }

  private changeStatus(
    id: string,
    action: 'publish' | 'pause' | 'unpublish' | 'archive',
  ): Observable<PlatformAnnouncement> {
    return this.http
      .post<{
        announcement: PlatformAnnouncement;
      }>(
        `${this.adminUrl}/${encodeURIComponent(id)}/${action}`,
        {},
        {
          headers: skipLoaderHeaders(),
        },
      )
      .pipe(map((response) => response.announcement));
  }

  stats(id: string): Observable<AnnouncementStats> {
    return this.http.get<AnnouncementStats>(
      `${this.adminUrl}/${encodeURIComponent(id)}/stats`,
      { headers: skipLoaderHeaders() },
    );
  }

  // --- End user ---

  pending(platform: AnnouncementPlatform): Observable<PendingAnnouncement[]> {
    return this.http
      .get<{ announcements: PendingAnnouncement[] }>(
        `${this.userUrl}/pending`,
        {
          params: new HttpParams().set('platform', platform),
          headers: skipLoaderHeaders(),
        },
      )
      .pipe(map((response) => response.announcements ?? []));
  }

  history(platform: AnnouncementPlatform): Observable<HistoryAnnouncement[]> {
    return this.http
      .get<{
        announcements: HistoryAnnouncement[];
      }>(`${this.userUrl}/history`, {
        params: new HttpParams().set('platform', platform),
        headers: skipLoaderHeaders(),
      })
      .pipe(map((response) => response.announcements ?? []));
  }

  markSeen(id: string, platform: AnnouncementPlatform): Observable<void> {
    return this.postState(id, 'seen', platform);
  }

  snooze(id: string, platform: AnnouncementPlatform): Observable<void> {
    return this.postState(id, 'snooze', platform);
  }

  acknowledge(id: string, platform: AnnouncementPlatform): Observable<void> {
    return this.postState(id, 'acknowledge', platform);
  }

  private postState(
    id: string,
    action: 'seen' | 'snooze' | 'acknowledge',
    platform: AnnouncementPlatform,
  ): Observable<void> {
    return this.http
      .post<void>(
        `${this.userUrl}/${encodeURIComponent(id)}/${action}`,
        { platform },
        { headers: skipLoaderHeaders() },
      )
      .pipe(map(() => undefined));
  }
}
